// Modo de centro de costo por empresa (autenticacion.empresas.centro_costo_modo):
// 'partner'  → el centro de costo es un financiero.partner (tipo_partner incluye 'centro_costo'); comportamiento histórico.
// 'contable' → el centro de costo es contabilidad.centros_costo (catálogo propio de Contabilidad).
// Se usa en Procesar (recibidos/emitidos) y en Facturación para decidir qué combo
// mostrar y qué parámetro enviar a las RPCs de grabado/emisión.
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';

export type CentroCostoModo = 'partner' | 'contable';

const CONT = { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } };

export interface CentroContableOpt { id: string; codigo: string; nombre: string }

/** Modo de centro de costo de la empresa actual (default 'partner' si no está configurado). */
export function useCentroCostoModo() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;
  const { data, isLoading } = useQuery<CentroCostoModo>({
    queryKey: ['fin-centro-costo-modo', empresaId],
    enabled: !!empresaId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const rows = (await apiClient.get<{ centro_costo_modo: string | null }[]>(
        `/rest/autenticacion.empresas?id=eq.${empresaId}&select=centro_costo_modo`)).data;
      return (rows[0]?.centro_costo_modo as CentroCostoModo) || 'partner';
    },
  });
  return { modo: data ?? 'partner', isLoading } as const;
}

/** Catálogo de centros de costo contables (contabilidad.centros_costo) para el combo en modo 'contable'. */
export function useCentrosContables(enabled = true) {
  return useQuery<CentroContableOpt[]>({
    queryKey: ['cont-centros'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await apiClient.post('/rpc/cont_centros', {}, CONT)).data || [],
  });
}

/** Etiqueta del campo de centro de costo según el modo de la empresa. */
export const labelCentroCosto = (modo: CentroCostoModo) =>
  modo === 'contable' ? 'Centro de costo (contable)' : 'Centro de costo';
